/// <amd-module name="VSSUI/Components/Table/ColumnMore" />
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ExpandableButton } from "VSSUI/Button";
import { IExpandable } from "VSSUI/Expandable";
import { ContextualMenu, IMenuProps } from "VSSUI/Menu";
import * as Resources from "VSSUI/Resources.Widgets";
import { KeyCode, css, eventTargetContainsNode, preventDefault } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import * as React from "react";
import { IMeasurementStyle, ITableColumn, TableColumnLayout } from "./Table.Props";

/**
 * Standard widths for the more column
 */
export const enum ColumnMoreWidths {
    compact = 2,
    default = 2.625
}

/**
 * The ColumnMore class is used as a custom column within a Table.
 *
 * It renders a fixed width column with a button that is visible on focus and
 * hover that will show a contextual menu for the given item. The caller
 * should create a new instance of this for each table. The header for this
 * column is empty.
 *
 * When creating the column you must supply delegate that is used to get the
 * menuProps when the menu is opened.
 */
export class ColumnMore<T> implements ITableColumn<T> {
    private menuAvailable?: (listItem: T) => boolean;
    private menuProvider: (listItem: T, rowIndex: number, buttonRef: React.RefObject<ExpandableButton>) => IMenuProps;
    private onActivate?: (rowIndex: number, columnIndex: number, event?: React.MouseEvent | React.KeyboardEvent) => void;
    public ariaLabel = Resources.MoreActions;
    public columnLayout = TableColumnLayout.none;
    public id = "_more";
    public width: ObservableValue<number>;
    public widthStyle = IMeasurementStyle.REM;
    private refs?: ObservableArray<React.RefObject<ExpandableButton>>;

    /**
     * Creates an instance of ColumnMore with the appropriate delegates.
     *
     * @param menuProvider Method that returns the menu props. This is called
     *  with the item from the row the menu was activated on.
     * @param menuAvailable Optional method that is called when the row
     *  is rendered. The MoreButton will be shown if no method is supplied
     *  or if the method is supplied and it returns true.
     */
    constructor(
        menuProvider: (listItem: T, rowIndex: number, buttonRef: React.RefObject<ExpandableButton>) => IMenuProps,
        menuAvailable?: (listItem: T) => boolean,
        onActivate?: (rowIndex: number, columnIndex: number, event?: React.MouseEvent | React.KeyboardEvent) => void,
        width: number = ColumnMoreWidths.default,
        refs?: ObservableArray<React.RefObject<ExpandableButton>>
    ) {
        this.menuAvailable = menuAvailable;
        this.menuProvider = menuProvider;
        this.onActivate = onActivate;
        this.width = new ObservableValue(width);
        this.refs = refs;
    }

    public renderCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<T>, listItem: T): JSX.Element => {
        const buttonRef = React.createRef<ExpandableButton>();
        if (this.refs && !this.refs.value[rowIndex]) {
            this.refs.value[rowIndex] = buttonRef;
        }
        return (
            <td
                aria-colindex={columnIndex + 1}
                className={css("bolt-table-cell-side-action bolt-table-cell bolt-list-cell", "col-" + columnIndex)}
                data-column-index={columnIndex}
                key="col-more"
                onClick={this.onClick}
                onDoubleClick={preventDefault}
                onKeyDown={this.onKeyDown}
                onMouseDown={preventDefault}
            >
                {!this.menuAvailable || this.menuAvailable(listItem) ? (
                    <div className="bolt-table-cell-content-reveal flex-row justify-center">
                        <ExpandableButton
                            ariaLabel={Resources.MoreTooltip}
                            className="bolt-table-button-more"
                            excludeTabStop={true}
                            hideDropdownIcon={true}
                            iconProps={{ className: "small", iconName: "MoreVertical" }}
                            onClick={e => {
                                if (this.onActivate) {
                                    this.onActivate(rowIndex, columnIndex, e);
                                }
                            }}
                            ref={this.refs?.value[rowIndex]}
                            renderCallout={(dropdown: IExpandable, dropdownId: string, anchorElement?: HTMLElement): JSX.Element => {
                                return (
                                    <ContextualMenu
                                        anchorElement={anchorElement}
                                        anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                                        menuProps={{...this.menuProvider(listItem, rowIndex, buttonRef), id: dropdownId }}
                                        menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
                                        onDismiss={dropdown.collapse}
                                    />
                                );
                            }}
                            subtle={true}
                            tooltipProps={{ text: Resources.MoreTooltip }}
                        />
                    </div>
                ) : (
                    <span className="bolt-table-cell-content visually-hidden">{Resources.NoMoreActions}</span>
                )}
            </td>
        );
    };

    public renderHeaderCell = (columnIndex: number, tableColumn: ITableColumn<T>): JSX.Element => {
        return (
            <th
                aria-colindex={columnIndex + 1}
                aria-label={tableColumn.ariaLabel}
                className={css(tableColumn.headerClassName, "bolt-table-header-cell", "col-header-" + columnIndex)}
                data-column-index={columnIndex}
                key="col-more"
            >
                <span className="visually-hidden">{tableColumn.ariaLabel}</span>
            </th>
        );
    };

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (!eventTargetContainsNode(event, ["A"], document.body)) {
                event.preventDefault();
            }
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                if (!eventTargetContainsNode(event, ["A"])) {
                    event.preventDefault();
                }
            }
        }
    };
}
